import { Link } from 'react-router-dom';
import { 
  Bug, Shield, Rat, Home as HomeIcon, Zap, Sparkles, Building2, 
  CheckCircle, ArrowRight, Phone, Leaf
} from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Services() {
  const services = [
    {
      id: 'general',
      icon: Bug,
      name: 'General Pest Control',
      description: 'Comprehensive protection against common household pests including ants, spiders, and other crawling insects.',
      problems: ['Spider webs and egg sacs', 'Ant trails in kitchen', 'Silverfish in bathrooms', 'Earwig infestations'],
      process: ['Initial inspection', 'Identify pest entry points', 'Apply targeted treatments', 'Set up prevention barriers', 'Follow-up monitoring'],
      benefits: ['Year-round protection', 'Safe for families and pets', 'Preventive treatments', 'Guaranteed results'],
      image: 'https://images.unsplash.com/photo-1584820927498-cfe5211fd8bf?w=600&h=400&fit=crop'
    },
    {
      id: 'termite',
      icon: HomeIcon,
      name: 'Termite Control',
      description: 'Protect your biggest investment from costly termite damage with our advanced termite elimination and prevention services.',
      problems: ['Wood damage in structures', 'Mud tubes on foundation', 'Hollow-sounding wood', 'Discarded wings near windows'],
      process: ['Comprehensive property inspection', 'Identify termite species', 'Baiting system installation', 'Liquid barrier treatment', 'Annual monitoring'],
      benefits: ['Prevents structural damage', 'Long-term protection plans', 'Property value preservation', 'Transferable warranty'],
      image: 'https://images.unsplash.com/photo-1762620944311-1f9b05a51e1a?crop=entropy&cs=srgb&fm=jpg&w=600&h=400&fit=crop'
    },
    {
      id: 'rodent',
      icon: Rat,
      name: 'Rodent Control',
      description: 'Safe and humane removal of mice and rats from your property, plus exclusion services to keep them out for good.',
      problems: ['Droppings in cabinets', 'Gnaw marks on wires', 'Scratching sounds at night', 'Food contamination'],
      process: ['Property assessment', 'Identify entry points', 'Set humane traps', 'Exclusion work', 'Sanitation guidance'],
      benefits: ['Humane removal methods', 'Prevents disease spread', 'Protects wiring and insulation', 'Entry point sealing'],
      image: 'https://images.unsplash.com/photo-1425082661705-1834bfd09dca?w=600&h=400&fit=crop'
    },
    {
      id: 'bedbug',
      icon: Shield,
      name: 'Bed Bug Treatment',
      description: 'Complete elimination of bed bug infestations using heat treatment and targeted chemical applications.',
      problems: ['Bites on skin', 'Blood stains on sheets', 'Musty odor in bedroom', 'Visible bugs in mattress seams'],
      process: ['Thorough inspection', 'Prepare treatment area', 'Heat treatment application', 'Chemical barrier application', 'Post-treatment inspection'],
      benefits: ['100% elimination guarantee', 'Same-day treatment available', 'Safe for mattresses', 'Discreet service'],
      image: 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=600&h=400&fit=crop'
    },
    {
      id: 'ant',
      icon: Bug,
      name: 'Ant Control',
      description: 'Eliminate ant colonies at the source with our targeted baiting and barrier treatments.',
      problems: ['Kitchen invasions', 'Carpenter ant damage', 'Fire ant mounds', 'Trail formations'],
      process: ['Species identification', 'Locate nest locations', 'Bait placement', 'Perimeter treatment', 'Colony elimination'],
      benefits: ['Targets entire colony', 'Quick results', 'Prevents re-infestation', 'Indoor and outdoor solutions'],
      image: 'https://images.pexels.com/photos/32610024/pexels-photo-32610024.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop'
    },
    {
      id: 'cockroach',
      icon: Zap,
      name: 'Cockroach Control',
      description: 'Fast-acting solutions for cockroach problems using gel baits, growth regulators, and residual treatments.',
      problems: ['Nighttime sightings', 'Egg casings', 'Musty smell', 'Allergic reactions'],
      process: ['Deep inspection', 'Identify harborage areas', 'Gel bait application', 'Crack and crevice treatment', 'Growth regulator use'],
      benefits: ['Rapid knockdown', 'Breaks breeding cycle', 'Safe for food areas', 'Long-lasting protection'],
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop'
    },
    {
      id: 'mosquito',
      icon: Sparkles,
      name: 'Mosquito Control',
      description: 'Enjoy your outdoor spaces pest-free with our comprehensive mosquito reduction programs.',
      problems: ['Itchy bites', 'Unable to enjoy yard', 'Standing water breeding', 'Disease concerns'],
      process: ['Property survey', 'Identify breeding sites', 'Larvicide application', 'Adulticide treatment', 'Ongoing management'],
      benefits: ['Yard-wide protection', 'Safe for pets and plants', 'Special event treatments', 'Monthly programs available'],
      image: 'https://images.unsplash.com/photo-1765980086761-8c6ec5858edf?crop=entropy&cs=srgb&fm=jpg&w=600&h=400&fit=crop'
    },
    {
      id: 'commercial',
      icon: Building2,
      name: 'Commercial Pest Control',
      description: 'Tailored pest management solutions for businesses of all sizes, ensuring compliance and customer satisfaction.',
      problems: ['Health code violations', 'Customer complaints', 'Employee concerns', 'Product contamination'],
      process: ['Facility assessment', 'Custom treatment plan', 'IPM implementation', 'Documentation and reporting', 'Regular service visits'],
      benefits: ['Compliance documentation', 'Minimal business disruption', 'Industry-specific solutions', 'Emergency response'],
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&h=400&fit=crop'
    }
  ];

  return (
    <div className="page-transition pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="section-padding bg-[#F7F9F7]" data-testid="services-hero">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#E8F5E9] text-[#2E7D32] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Leaf className="w-4 h-4" />
              Professional Services
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#1A1C1A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Our Pest Control Services
            </h1>
            <p className="text-lg text-[#52525B]">
              From common household pests to specialized commercial needs, we offer comprehensive 
              solutions to keep your property pest-free and safe.
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="section-padding">
        <div className="container-main">
          <div className="space-y-16 lg:space-y-24">
            {services.map((service, index) => (
              <div 
                key={service.id} 
                id={service.id}
                className={`grid lg:grid-cols-2 gap-8 lg:gap-16 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                data-testid={`service-detail-${service.id}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="inline-flex items-center gap-2 bg-[#E8F5E9] text-[#2E7D32] px-3 py-1.5 rounded-full text-sm font-medium mb-4">
                    <service.icon className="w-4 h-4" />
                    {service.name}
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1C1A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {service.name}
                  </h2>
                  <p className="text-lg text-[#52525B] mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Problems Solved */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-[#1A1C1A] mb-3">Problems We Solve:</h3>
                    <ul className="grid grid-cols-2 gap-2">
                      {service.problems.map((problem, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-[#52525B]">
                          <CheckCircle className="w-4 h-4 text-[#2E7D32] flex-shrink-0" />
                          {problem}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Treatment Process */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-[#1A1C1A] mb-3">Our Process:</h3>
                    <div className="flex flex-wrap gap-2">
                      {service.process.map((step, i) => (
                        <span key={i} className="bg-[#F7F9F7] text-[#52525B] px-3 py-1 rounded-full text-sm">
                          {i + 1}. {step}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Benefits */}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-[#1A1C1A] mb-3">Benefits:</h3>
                    <ul className="space-y-2">
                      {service.benefits.map((benefit, i) => (
                        <li key={i} className="flex items-center gap-2 text-[#52525B]">
                          <div className="w-2 h-2 bg-[#2E7D32] rounded-full"></div>
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link to="/contact">
                      <Button className="btn-primary w-full sm:w-auto" data-testid={`cta-${service.id}`}>
                        Get Free Quote
                        <ArrowRight className="w-5 h-5" />
                      </Button>
                    </Link>
                    <a href="tel:+18883568891" className="btn-accent w-full sm:w-auto text-center">
                      <Phone className="w-5 h-5" />
                      Call Now
                    </a>
                  </div>
                </div>
                <div className={`rounded-2xl overflow-hidden shadow-xl img-zoom ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <img 
                    src={service.image} 
                    alt={service.name}
                    className="w-full h-[300px] sm:h-[400px] object-cover"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-gradient py-16 sm:py-20" data-testid="services-cta">
        <div className="container-main text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Need Help Identifying Your Pest Problem?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Our expert technicians can identify any pest and recommend the best treatment plan. 
            Schedule your free inspection today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-[#2E7D32] hover:bg-white/90 rounded-full px-8 py-4 font-semibold shadow-lg">
                Schedule Free Inspection
              </Button>
            </Link>
            <a href="tel:+18883568891" className="btn-accent">
              <Phone className="w-5 h-5" />
              Call: +1-888-356-8891
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
